/**
 * The Monitor's reads as its views show them: alert DTOs as inbox rows, a case's watchlists as
 * one list of watched things. Pure, so the page and the stories map fixtures the same way.
 */
import {
  type AlertInboxRow,
  MONITOR_RULE_KINDS,
  type MonitorRuleKind,
} from '@/components/monitor/AlertInbox'
import type { LoadView } from '@/components/monitor/MonitorSection'
import type { AlertDto, WatchlistDto } from '@/services/api/case-monitor'
import type { CaseRole } from '@/services/api/cases'
import { alertObject, ruleSeverity } from './alert-findings'

export function ruleKind(kind: string): MonitorRuleKind | null {
  return (MONITOR_RULE_KINDS as readonly string[]).includes(kind) ? (kind as MonitorRuleKind) : null
}

/** A list's name as its rules are named (ADR-0100); null for the default list, which the page names. */
export function listName(watchlist: WatchlistDto): string | null {
  return watchlist.is_default ? null : watchlist.name
}

/** One row per alert, in the order the server sent them (newest learned first). */
export function toInboxRows(alerts: readonly AlertDto[]): AlertInboxRow[] {
  return alerts.map(alert => ({
    id: alert.id,
    ruleId: alert.rule_id,
    subjectLabel: alert.subject_label,
    subjectType: alert.subject_type,
    ruleKind: ruleKind(alert.rule_kind),
    ruleName: alert.rule_name,
    severity: ruleSeverity(alert.severity),
    ...alertObject(alert),
    documentId: alert.document_id,
    documentName: alert.document_name,
    learnedAt: alert.recorded_at,
    hits: alert.hits,
    isNew: alert.state === 'new',
  }))
}

/** Whether *Watch* put the entity `uid` on the case's default list; a rule that picked it does not count. */
export function isWatched(watchlists: readonly WatchlistDto[], uid: string): boolean {
  return watchlists.some(
    watchlist => watchlist.is_default && watchlist.items.some(item => item.entity_uid === uid)
  )
}

/** Collaborators and owners change what is watched; a viewer reads (R-B1). */
export function canChangeWatching(role: CaseRole | undefined): boolean {
  return role === 'collaborator' || role === 'owner'
}

/** A read as a section shows it; rows already read stay on screen when a refetch fails. */
export function toLoadView<D, T>(
  read: { data: D | undefined; isError: boolean },
  rows: (data: D) => readonly T[]
): LoadView<T> {
  if (read.data !== undefined) return { status: 'ready', rows: rows(read.data) }
  return read.isError ? { status: 'failed' } : { status: 'loading' }
}
